import React, { useState, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const DailyClosureTab = () => {
  // State
  const [dailyClosureReports, setDailyClosureReports] = useState([]);
  const [dailyClosureSummary, setDailyClosureSummary] = useState({});
  const [dailyClosureFilters, setDailyClosureFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    cashier: ''
  });
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [showClosureDetails, setShowClosureDetails] = useState(false);
  const [selectedClosureReport, setSelectedClosureReport] = useState(null);
  const [showClosureReportModal, setShowClosureReportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load daily closure reports via shiftEnhancedService
  const loadDailyClosureReports = useCallback(async () => {
    try {
      setLoading(true);

      const shiftEnhancedService = (await import('../../../services/shiftEnhancedService')).default;
      const response = await shiftEnhancedService.getDailyClosureReports(dailyClosureFilters);

      if (response.success) {
        const reports = response.data.reports || [];
        const summary = response.data.summary || {};

        setDailyClosureReports(reports);
        setDailyClosureSummary(summary);
      } else {
        console.error('Blad w odpowiedzi API:', response.message);
        setError(response.message || 'Blad ladowania raportow zamkniec');
      }
    } catch (err) {
      console.error('Blad ladowania raportow zamkniec:', err);
      setError('Blad ladowania raportow zamkniec');
    } finally {
      setLoading(false);
    }
  }, [dailyClosureFilters]);

  // Load on mount
  useEffect(() => {
    loadDailyClosureReports();
  }, []);

  // View closure details with full API data
  const viewClosureDetails = async (closure) => {
    try {
      const response = await api.get(`/admin/daily-closure-reports/${closure.id}`);
      const data = response.data;

      if (data.success && data.data?.report) {
        setSelectedClosure(data.data.report);
      } else {
        setSelectedClosure(closure);
      }
    } catch (error) {
      console.error('viewClosureDetails - Blad pobierania szczegolow:', error);
      setSelectedClosure(closure);
    }
    setShowClosureDetails(true);
  };

  // Export daily closures as CSV
  const exportDailyClosures = (closures) => {
    if (!closures || closures.length === 0) {
      toast.error('Brak raportow do eksportu');
      return;
    }

    const csvContent = [
      ['Data', 'Kasjer', 'Kasa System', 'Kasa Fizyczna', 'Roznica Kasa', 'Terminal System', 'Terminal Rzeczywisty', 'Roznica Terminal', 'Kasa Fiskalna', 'Sprzedaz Gotowka', 'Sprzedaz Karta', 'Liczba Transakcji', 'TikTok', 'Facebook', 'Instagram', 'Google', 'Osiagniecia Sprzedaz', 'Osiagniecia Praca'],
      ...closures.map(report => [
        report.data_zamkniecia,
        report.kasjer_login,
        report.kasa_system,
        report.kasa_fizyczna,
        report.roznica_kasa,
        report.terminal_system,
        report.terminal_rzeczywisty,
        report.roznica_terminal,
        report.kasa_fiskalna_raport,
        report.sprzedaz_gotowka,
        report.sprzedaz_karta,
        report.liczba_transakcji,
        report.social_media_tiktok || '',
        report.social_media_facebook || '',
        report.social_media_instagram || '',
        report.social_media_google || '',
        report.osiagniecia_sprzedaz || '',
        report.osiagniecia_praca || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `raporty_zamkniecia_dnia_${dailyClosureFilters.date_from}_${dailyClosureFilters.date_to}.csv`;
    link.click();
  };

  // PDF export via backend
  const exportDailyClosuresPDF = async (closures) => {
    if (!closures || closures.length === 0) {
      toast.error('Brak raportow do eksportu');
      return;
    }

    try {
      const params = new URLSearchParams({
        date_from: dailyClosureFilters.date_from,
        date_to: dailyClosureFilters.date_to
      });
      if (dailyClosureFilters.cashier) {
        params.append('cashier', dailyClosureFilters.cashier);
      }

      const response = await api.get(`/admin/daily-closure-reports/pdf?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `raporty_zamkniecia_dnia_${dailyClosureFilters.date_from}_${dailyClosureFilters.date_to}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Blad generowania PDF:', err);
      toast.error('Blad generowania PDF.');
    }
  };

  // Drukuj pojedynczy raport zamkniecia (PDF dla danego dnia/kasjera)
  const printClosureReport = async (closure) => {
    if (!closure) return;

    try {
      const day = String(closure.data_zamkniecia || '').split('T')[0].split(' ')[0];
      const params = new URLSearchParams({ date_from: day, date_to: day });
      if (closure.kasjer_login) {
        params.append('cashier', closure.kasjer_login);
      }

      const response = await api.get(`/admin/daily-closure-reports/pdf?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (!printWindow) {
        // Popup zablokowany - pobierz plik zamiast otwierac
        const link = document.createElement('a');
        link.href = url;
        link.download = `raport_zamkniecia_${day}.pdf`;
        link.click();
      }
    } catch (err) {
      console.error('Blad drukowania raportu:', err);
      toast.error('Blad generowania raportu do druku.');
    }
  };

  // Eksport pojedynczego zamkniecia (CSV) - reuzycie eksportu listy
  const exportSingleClosure = (closure) => {
    if (!closure) return;
    exportDailyClosures([closure]);
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Lista zamkniec dziennych */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '4px',
        backgroundColor: 'var(--card-bg)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          padding: '0.4rem 0.6rem',
          background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
          borderBottom: '1px solid var(--border)',
          fontWeight: '600',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <div>
            <span style={{ fontSize: '11px' }}>📋 Historia zamkniec dziennych</span>
            <div style={{ fontSize: '9px', fontWeight: '400', opacity: 0.9, marginTop: '2px' }}>
              💰 Finanse • 📊 Roznice • 📱 Social Media • 🎯 Osiagniecia • 📝 Notatki
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px' }}>Od:</span>
            <input
              type="date"
              value={dailyClosureFilters.date_from}
              onChange={(e) => setDailyClosureFilters(prev => ({ ...prev, date_from: e.target.value }))}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            />
            <span style={{ fontSize: '11px' }}>Do:</span>
            <input
              type="date"
              value={dailyClosureFilters.date_to}
              onChange={(e) => setDailyClosureFilters(prev => ({ ...prev, date_to: e.target.value }))}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            />
            <input
              type="text"
              value={dailyClosureFilters.cashier}
              onChange={(e) => setDailyClosureFilters(prev => ({ ...prev, cashier: e.target.value }))}
              placeholder="Kasjer..."
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                width: '90px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            />
            <button
              onClick={loadDailyClosureReports}
              style={{
                padding: '4px 10px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                fontSize: '11px'
              }}
            >
              🔍 Szukaj
            </button>
            <button
              onClick={() => exportDailyClosures(dailyClosureReports)}
              style={{
                padding: '4px 10px',
                border: '1px solid var(--success)',
                color: 'var(--success)',
                fontSize: '11px'
              }}
            >
              📤 CSV
            </button>
            <button
              onClick={() => exportDailyClosuresPDF(dailyClosureReports)}
              style={{
                padding: '4px 10px',
                border: '1px solid var(--danger)',
                color: 'var(--danger)',
                fontSize: '11px'
              }}
            >
              📄 PDF
            </button>
          </div>
        </div>

        {/* Legenda wskaznikow */}
        <div style={{
          padding: '4px 8px',
          backgroundColor: 'var(--border)',
          borderBottom: '1px solid var(--border)',
          fontSize: '9px',
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <strong>Legenda:</strong>
          <span style={{ backgroundColor: 'var(--purple-light)', padding: '1px 4px', borderRadius: '2px' }}>📱 Social Media</span>
          <span style={{ backgroundColor: 'var(--warning-light)', padding: '1px 4px', borderRadius: '2px' }}>🎯 Osiagniecia</span>
          <span style={{ backgroundColor: 'var(--info-light)', padding: '1px 4px', borderRadius: '2px' }}>📝 Notatki</span>
          <span style={{ fontStyle: 'italic' }}>- kliknij "👁️" aby zobaczyc pelne dane</span>
        </div>

        <div className="admin-table-wrap" style={{ overflow: 'auto', maxHeight: '500px' }}>
          {dailyClosureReports && dailyClosureReports.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
              <thead style={{ backgroundColor: 'var(--border)', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    📅 Data / Kasjer
                  </th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    💵 Gotowka
                  </th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    💳 Karty
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    📊 Roznice
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    🧾 Trans.
                  </th>
                  <th className="col-hide-mobile" style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    📋 Dane
                  </th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                    ⚙️ Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {dailyClosureReports.map((report, index) => (
                  <tr key={report.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '5px 8px', fontSize: '10px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: '600', color: 'var(--text)' }}>
                        {new Date(report.data_zamkniecia).toLocaleDateString('pl-PL')}
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        👤 {report.kasjer_login || 'System'} • ⏰ {report.czas_zamkniecia?.split('.')[0] || ''}
                      </div>
                    </td>
                    <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '600', color: 'var(--success)' }}>
                        {parseFloat(report.kasa_fizyczna || 0).toFixed(2)} zl
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        sys: {parseFloat(report.kasa_system || 0).toFixed(2)} zl
                      </div>
                    </td>
                    <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '600', color: 'var(--info)' }}>
                        {parseFloat(report.sprzedaz_karta || 0).toFixed(2)} zl
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        term: {parseFloat(report.terminal_rzeczywisty || 0).toFixed(2)} zl
                      </div>
                    </td>
                    <td className="col-hide-mobile" style={{
                      padding: '5px 8px',
                      fontSize: '10px',
                      textAlign: 'right',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap'
                    }}>
                      <div style={{
                        fontWeight: '600',
                        color: (report.roznica_kasa || 0) < 0 ? 'var(--danger)' : (report.roznica_kasa || 0) > 0 ? 'var(--success)' : 'var(--text-secondary)'
                      }}>
                        Kasa: {parseFloat(report.roznica_kasa || 0).toFixed(2)} zl
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: (report.roznica_terminal || 0) < 0 ? 'var(--danger)' : (report.roznica_terminal || 0) > 0 ? 'var(--success)' : 'var(--text-muted)'
                      }}>
                        Term: {parseFloat(report.roznica_terminal || 0).toFixed(2)} zl
                      </div>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <span style={{
                        padding: '2px 6px',
                        backgroundColor: 'var(--info-light)',
                        color: 'var(--info)',
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {report.liczba_transakcji || 0}
                      </span>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                        {(report.social_media_tiktok || report.social_media_facebook ||
                          report.social_media_instagram || report.social_media_google) && (
                          <span style={{
                            padding: '2px 4px',
                            backgroundColor: 'var(--purple-light)',
                            color: 'var(--purple)',
                            borderRadius: '2px',
                            fontSize: '9px'
                          }} title="Social Media">
                            📱
                          </span>
                        )}
                        {(report.osiagniecia_sprzedaz || report.osiagniecia_praca) && (
                          <span style={{
                            padding: '2px 4px',
                            backgroundColor: 'var(--warning-light)',
                            color: 'var(--warning)',
                            borderRadius: '2px',
                            fontSize: '9px'
                          }} title="Osiagniecia">
                            🎯
                          </span>
                        )}
                        {report.uwagi_zamkniecia && (
                          <span style={{
                            padding: '2px 4px',
                            backgroundColor: 'var(--info-light)',
                            color: 'var(--info)',
                            borderRadius: '2px',
                            fontSize: '9px'
                          }} title="Notatki">
                            📝
                          </span>
                        )}
                        {!(report.social_media_tiktok || report.social_media_facebook ||
                          report.social_media_instagram || report.social_media_google ||
                          report.osiagniecia_sprzedaz || report.osiagniecia_praca || report.uwagi_zamkniecia) && (
                          <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>—</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '3px', justifyContent: 'center' }}>
                        <button
                          onClick={() => viewClosureDetails(report)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--info)',
                            color: 'var(--info)',
                            fontSize: '11px'
                          }}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => printClosureReport(report)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--success)',
                            color: 'var(--success)',
                            fontSize: '11px'
                          }}
                        >
                          🖨️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '1.5rem',
              color: 'var(--text-muted)'
            }}>
              <p style={{ margin: 0, fontSize: '11px' }}>📭 Brak raportow zamkniec dla wybranych kryteriow</p>
              <p style={{ fontSize: '10px', marginTop: '0.3rem', color: 'var(--text-muted)' }}>
                Uzyj filtrow powyzej aby wygenerowac raporty zamkniec dziennych
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal szczegolow zamkniecia */}
      {showClosureDetails && selectedClosure && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '8px',
            maxWidth: '700px',
            width: '95%',
            maxHeight: '85vh',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            {/* Naglowek modalu */}
            <div style={{
              background: 'linear-gradient(135deg, var(--info) 0%, var(--info) 100%)',
              color: '#fff',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>
                  📊 Szczegoly zamkniecia dziennego
                </h3>
                <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '2px' }}>
                  📅 {new Date(selectedClosure.data_zamkniecia).toLocaleDateString('pl-PL')} • 👤 {selectedClosure.kasjer_login || selectedClosure.uzytkownik_nazwa || 'System'}
                </div>
              </div>
              <button
                onClick={() => setShowClosureDetails(false)}
                style={{
                  padding: '4px 10px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  fontSize: '11px'
                }}
              >
                ✕ Zamknij
              </button>
            </div>

            <div style={{ padding: '0.5rem', overflow: 'auto', maxHeight: 'calc(85vh - 80px)' }}>
              <div style={{ display: 'grid', gap: '0.4rem' }}>

              {/* Podstawowe informacje */}
              <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', borderLeft: '3px solid var(--text-muted)' }}>
                <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                  📅 Informacje podstawowe
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.3rem', fontSize: '10px' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Data zamkniecia:</span><br/>
                    <strong>{new Date(selectedClosure.data_zamkniecia).toLocaleString('pl-PL')}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Uzytkownik:</span><br/>
                    <strong>{selectedClosure.uzytkownik_nazwa || selectedClosure.kasjer_login || 'System'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Okres:</span><br/>
                    <strong>{selectedClosure.data_od ? new Date(selectedClosure.data_od).toLocaleDateString('pl-PL') : '—'} - {selectedClosure.data_do ? new Date(selectedClosure.data_do).toLocaleDateString('pl-PL') : '—'}</strong>
                  </div>
                </div>
              </div>

              {/* Podsumowanie finansowe */}
              <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--info-light)', borderRadius: '4px', borderLeft: '3px solid var(--info)' }}>
                <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--info)' }}>
                  💰 Podsumowanie finansowe
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.3rem', fontSize: '10px' }}>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Kasa system:</span><br/>
                    <strong>{parseFloat(selectedClosure.kasa_system || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Kasa fizyczna:</span><br/>
                    <strong>{parseFloat(selectedClosure.kasa_fizyczna || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Roznica kasa:</span><br/>
                    <strong style={{
                      color: (selectedClosure.roznica_kasa || 0) < 0 ? 'var(--danger)' : (selectedClosure.roznica_kasa || 0) > 0 ? 'var(--success)' : 'var(--text-secondary)'
                    }}>
                      {parseFloat(selectedClosure.roznica_kasa || 0).toFixed(2)} zl
                    </strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Terminal system:</span><br/>
                    <strong>{parseFloat(selectedClosure.terminal_system || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Terminal rzeczywisty:</span><br/>
                    <strong>{parseFloat(selectedClosure.terminal_rzeczywisty || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Roznica terminal:</span><br/>
                    <strong style={{
                      color: (selectedClosure.roznica_terminal || 0) < 0 ? 'var(--danger)' : (selectedClosure.roznica_terminal || 0) > 0 ? 'var(--success)' : 'var(--text-secondary)'
                    }}>
                      {parseFloat(selectedClosure.roznica_terminal || 0).toFixed(2)} zl
                    </strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Kasa fiskalna:</span><br/>
                    <strong>{parseFloat(selectedClosure.kasa_fiskalna_raport || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Sprzedaz gotowka:</span><br/>
                    <strong style={{ color: 'var(--success)' }}>{parseFloat(selectedClosure.sprzedaz_gotowka || 0).toFixed(2)} zl</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Sprzedaz karta:</span><br/>
                    <strong style={{ color: 'var(--info)' }}>{parseFloat(selectedClosure.sprzedaz_karta || 0).toFixed(2)} zl</strong>
                  </div>
                </div>
              </div>

              {/* Statystyki transakcji */}
              <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--success-light)', borderRadius: '4px', borderLeft: '3px solid var(--success)' }}>
                <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--success)' }}>
                  📈 Statystyki transakcji
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.3rem', fontSize: '10px' }}>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Transakcje:</span><br/>
                    <strong style={{ fontSize: '12px', color: 'var(--success)' }}>{selectedClosure.liczba_transakcji || 0}</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Kasjer:</span><br/>
                    <strong>{selectedClosure.kasjer_login || 'System'}</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Rozpoczecie:</span><br/>
                    <strong>{selectedClosure.czas_rozpoczecia || '—'}</strong>
                  </div>
                  <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Zamkniecie:</span><br/>
                    <strong>{selectedClosure.czas_zamkniecia?.split('.')[0] || '—'}</strong>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              {(selectedClosure.social_media_tiktok || selectedClosure.social_media_facebook ||
                selectedClosure.social_media_instagram || selectedClosure.social_media_google) && (
                <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--purple-light)', borderRadius: '4px', borderLeft: '3px solid var(--purple)' }}>
                  <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--purple)' }}>
                    📱 Aktywnosc w Social Media
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.3rem', fontSize: '10px' }}>
                    {selectedClosure.social_media_tiktok && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>🎵 TikTok:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.social_media_tiktok}
                        </div>
                      </div>
                    )}
                    {selectedClosure.social_media_facebook && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>📘 Facebook:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.social_media_facebook}
                        </div>
                      </div>
                    )}
                    {selectedClosure.social_media_instagram && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>📷 Instagram:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.social_media_instagram}
                        </div>
                      </div>
                    )}
                    {selectedClosure.social_media_google && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>🔍 Google Business:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.social_media_google}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Osiagniecia dnia */}
              {(selectedClosure.osiagniecia_sprzedaz || selectedClosure.osiagniecia_praca) && (
                <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--warning-light)', borderRadius: '4px', borderLeft: '3px solid var(--warning)' }}>
                  <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--warning)' }}>
                    🎯 Osiagniecia dnia
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.3rem', fontSize: '10px' }}>
                    {selectedClosure.osiagniecia_sprzedaz && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>💰 Sprzedaz:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.osiagniecia_sprzedaz}
                        </div>
                      </div>
                    )}
                    {selectedClosure.osiagniecia_praca && (
                      <div style={{ backgroundColor: 'var(--card-bg)', padding: '4px 6px', borderRadius: '3px' }}>
                        <strong>🏪 Praca w sklepie:</strong>
                        <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                          {selectedClosure.osiagniecia_praca}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Uwagi zamkniecia */}
              {selectedClosure.uwagi_zamkniecia && (
                <div style={{ padding: '0.4rem 0.5rem', backgroundColor: 'var(--info-light)', borderRadius: '4px', borderLeft: '3px solid var(--cyan)' }}>
                  <h4 style={{ margin: '0 0 0.3rem 0', fontSize: '11px', fontWeight: '600', color: 'var(--info)' }}>
                    📝 Uwagi dotyczace zamkniecia dnia
                  </h4>
                  <div style={{
                    padding: '4px 6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '3px',
                    fontSize: '10px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedClosure.uwagi_zamkniecia}
                  </div>
                </div>
              )}

              {/* Akcje */}
              <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'flex-end', marginTop: '0.3rem' }}>
                <button
                  onClick={() => printClosureReport(selectedClosure)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--success)',
                    color: 'var(--success)',
                    fontSize: '11px'
                  }}
                >
                  🖨️ Drukuj raport
                </button>
                <button
                  onClick={() => exportSingleClosure(selectedClosure)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--info)',
                    color: 'var(--info)',
                    fontSize: '11px'
                  }}
                >
                  📤 Eksportuj
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyClosureTab;
